import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../../../components/Layout';
import {
  chartOfAccountsService,
  GLAccountDTO,
  CreateGLAccountRequest,
} from '../../../../services/accountingService';
import { useCurrencyFormat } from '../../../../hooks/useCurrency';
import { Plus, Pencil, Trash2, Check, X, ListTree, ScrollText } from 'lucide-react';
import { getServerErrorMessage } from '../../../../utils/errorUtils';

const ACCOUNT_TYPES = ['Asset', 'Liability', 'Equity', 'Revenue', 'Expense'];

const emptyForm = (): CreateGLAccountRequest => ({
  code: '',
  name: '',
  description: '',
  accountType: 'Asset',
  parentAccountId: null,
  isActive: true,
});

const ChartOfAccountsPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const fmt = useCurrencyFormat();

  const [accounts,      setAccounts]      = useState<GLAccountDTO[]>([]);
  const [loading,       setLoading]       = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [error,         setError]         = useState<string | null>(null);
  const [success,       setSuccess]       = useState<string | null>(null);
  const [editingId,     setEditingId]     = useState<number | 'new' | null>(null);
  const [form,          setForm]          = useState<CreateGLAccountRequest>(emptyForm());
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const [includeInactive, setIncludeInactive] = useState(false);

  useEffect(() => { loadAccounts(); }, [includeInactive]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (success) {
      const timer = setTimeout(() => setSuccess(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [success]);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const res = await chartOfAccountsService.getAll(includeInactive);
      const data = (res?.data as any)?.data ?? res?.data ?? [];
      if (Array.isArray(data)) setAccounts(data);
    } catch {
      setError(t('accounting.coa.failedToLoad'));
    } finally {
      setLoading(false);
    }
  };

  const startNew = () => { setForm(emptyForm()); setEditingId('new'); setError(null); };

  const startEdit = (a: GLAccountDTO) => {
    setForm({
      code: a.code,
      name: a.name,
      description: a.description || '',
      accountType: a.accountType,
      parentAccountId: a.parentAccountId ?? null,
      isActive: a.isActive,
    });
    setEditingId(a.id);
    setError(null);
  };

  const cancelEdit = () => { setEditingId(null); setError(null); };

  const handleSave = async () => {
    if (!form.code.trim() || !form.name.trim()) {
      setError(t('accounting.coa.codeNameRequired'));
      return;
    }
    setSaving(true);
    setError(null);
    try {
      if (editingId === 'new') {
        await chartOfAccountsService.create(form);
      } else if (editingId != null) {
        await chartOfAccountsService.update(editingId, form);
      }
      setSuccess(t('accounting.coa.savedSuccess'));
      setEditingId(null);
      await loadAccounts();
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('accounting.coa.failedToSave'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number) => {
    setSaving(true);
    try {
      await chartOfAccountsService.delete(id);
      setSuccess(t('accounting.coa.deletedSuccess'));
      setConfirmDelete(null);
      await loadAccounts();
    } catch (err: any) {
      setError(getServerErrorMessage(err) ?? t('accounting.coa.failedToDelete'));
    } finally {
      setSaving(false);
    }
  };

  const typeGroups = ACCOUNT_TYPES.map((type) => ({
    type,
    accounts: accounts.filter((a) => a.accountType === type),
  })).filter((g) => g.accounts.length > 0);

  return (
    <Layout>
      <div style={{ padding: '0 20px 40px', maxWidth: 1000, margin: '0 auto' }}>

        <div className="ims-list-header">
          <h1 className="ims-list-title">{t('accounting.coa.title')}</h1>
          <div className="ims-list-actions">
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginInlineEnd: 8 }}>
              <input type="checkbox" checked={includeInactive} onChange={(e) => setIncludeInactive(e.target.checked)} />
              {t('accounting.coa.showInactive')}
            </label>
            <button className="btn-new" onClick={startNew} disabled={editingId != null}>
              <Plus size={16} strokeWidth={2} />
              {t('accounting.coa.addNew')}
            </button>
          </div>
        </div>

        {success && (
          <div className="ims-alert ims-alert-success" style={{ marginBottom: 16 }}>
            <Check size={15} /> {success}
            <button type="button" onClick={() => setSuccess(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
          </div>
        )}
        {error && (
          <div className="ims-alert ims-alert-error" style={{ marginBottom: 16 }}>
            <X size={15} /> {error}
            <button type="button" onClick={() => setError(null)} style={{ marginInlineStart: 'auto', background: 'none', border: 'none', cursor: 'pointer' }}>✕</button>
          </div>
        )}

        {editingId != null && (
          <div className="ims-card" style={{ marginBottom: 20 }}>
            <div className="ims-card-header">
              <h2 className="ims-card-title">{editingId === 'new' ? t('accounting.coa.createTitle') : t('accounting.coa.editTitle')}</h2>
            </div>
            <div className="ims-card-body">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
                <div className="ims-form-group" style={{ marginBottom: 10 }}>
                  <label className="ims-form-label" style={{ fontSize: 12 }}>{t('accounting.coa.code')}</label>
                  <input type="text" className="ims-form-control" value={form.code} maxLength={20}
                    onChange={(e) => setForm((prev) => ({ ...prev, code: e.target.value }))} />
                </div>
                <div className="ims-form-group" style={{ marginBottom: 10 }}>
                  <label className="ims-form-label" style={{ fontSize: 12 }}>{t('accounting.coa.name')}</label>
                  <input type="text" className="ims-form-control" value={form.name} maxLength={150}
                    onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))} />
                </div>
                <div className="ims-form-group" style={{ marginBottom: 10 }}>
                  <label className="ims-form-label" style={{ fontSize: 12 }}>{t('accounting.coa.accountType')}</label>
                  <select className="ims-form-control" value={form.accountType}
                    onChange={(e) => setForm((prev) => ({ ...prev, accountType: e.target.value }))}>
                    {ACCOUNT_TYPES.map((tpe) => <option key={tpe} value={tpe}>{t(`accounting.accountTypes.${tpe}`)}</option>)}
                  </select>
                </div>
                <div className="ims-form-group" style={{ marginBottom: 10 }}>
                  <label className="ims-form-label" style={{ fontSize: 12 }}>{t('accounting.coa.parentAccount')}</label>
                  <select className="ims-form-control" value={form.parentAccountId ?? ''}
                    onChange={(e) => setForm((prev) => ({ ...prev, parentAccountId: e.target.value ? Number(e.target.value) : null }))}>
                    <option value="">{t('accounting.coa.noParent')}</option>
                    {accounts.filter((a) => a.id !== editingId).map((a) => (
                      <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="ims-form-group" style={{ marginBottom: 10 }}>
                <label className="ims-form-label" style={{ fontSize: 12 }}>{t('accounting.coa.description')}</label>
                <textarea className="ims-form-control ims-textarea" rows={2} value={form.description || ''}
                  onChange={(e) => setForm((prev) => ({ ...prev, description: e.target.value }))} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, marginBottom: 16 }}>
                <input type="checkbox" checked={form.isActive}
                  onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))} />
                {t('accounting.coa.isActive')}
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="ims-btn ims-btn-primary" onClick={handleSave} disabled={saving}>
                  {saving ? <><span className="spinner-border spinner-border-sm me-1" role="status" />{t('common.saving')}</> : t('common.save')}
                </button>
                <button className="ims-btn ims-btn-secondary" onClick={cancelEdit} disabled={saving}>
                  {t('common.cancel')}
                </button>
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div style={{ textAlign: 'center', padding: 40 }}>
            <div className="spinner-border" role="status"><span className="visually-hidden">{t('common.loading')}</span></div>
          </div>
        ) : accounts.length === 0 ? (
          <div className="ims-card" style={{ textAlign: 'center', padding: 40, color: 'var(--ims-text-muted)' }}>
            <ListTree size={40} strokeWidth={1} style={{ marginBottom: 8 }} />
            <div>{t('accounting.coa.noAccounts')}</div>
          </div>
        ) : (
          typeGroups.map((group) => (
            <div className="ims-card" key={group.type} style={{ marginBottom: 16 }}>
              <div className="ims-card-header">
                <span className="ims-card-title">{t(`accounting.accountTypes.${group.type}`)}</span>
              </div>
              <div className="ims-table-scroll">
                <table className="ims-table">
                  <thead>
                    <tr>
                      <th className="ims-th" style={{ width: 90 }}>{t('accounting.coa.code')}</th>
                      <th className="ims-th">{t('accounting.coa.name')}</th>
                      <th className="ims-th ims-th--num" style={{ width: 140 }}>{t('accounting.coa.balance')}</th>
                      <th className="ims-th" style={{ width: 70 }}>{t('common.status')}</th>
                      <th className="ims-th" style={{ width: 110 }} />
                    </tr>
                  </thead>
                  <tbody>
                    {group.accounts.map((a) => (
                      <tr key={a.id} className="ims-tr">
                        <td className="ims-td"><code>{a.code}</code></td>
                        <td className="ims-td">
                          <div style={{ fontWeight: 600 }}>{a.name}</div>
                          {a.isSystemAccount && (
                            <div style={{ fontSize: 10, color: 'var(--ims-accent)' }}>{t('accounting.coa.systemAccount')}</div>
                          )}
                        </td>
                        <td className="ims-td ims-td--line-total">{fmt(a.currentBalance)}</td>
                        <td className="ims-td">
                          <span className={`ims-badge ${a.isActive ? 'ims-badge-completed' : 'ims-badge-closed'}`}>
                            {a.isActive ? t('common.active') : t('common.inactive')}
                          </span>
                        </td>
                        <td className="ims-td">
                          {confirmDelete === a.id ? (
                            <div style={{ display: 'flex', gap: 4 }}>
                              <button className="ims-btn ims-btn-danger" style={{ padding: '2px 8px', fontSize: 12 }}
                                onClick={() => handleDelete(a.id)} disabled={saving}>{t('common.confirm')}</button>
                              <button className="ims-btn ims-btn-secondary" style={{ padding: '2px 8px', fontSize: 12 }}
                                onClick={() => setConfirmDelete(null)}>{t('common.cancel')}</button>
                            </div>
                          ) : (
                            <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                              <button className="grid-action-btn-view" title={t('accounting.coa.viewLedger')}
                                onClick={() => navigate(`/accounting/chart-of-accounts/${a.id}/ledger`)}>
                                <ScrollText size={15} strokeWidth={1.75} />
                              </button>
                              <button className="grid-action-btn-edit" title={t('common.edit')} onClick={() => startEdit(a)}>
                                <Pencil size={15} strokeWidth={1.75} />
                              </button>
                              {!a.isSystemAccount && (
                                <button className="grid-action-btn-delete" title={t('common.delete')} onClick={() => setConfirmDelete(a.id)}>
                                  <Trash2 size={15} strokeWidth={1.75} />
                                </button>
                              )}
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ))
        )}
      </div>
    </Layout>
  );
};

export default ChartOfAccountsPage;
